import { useState, useEffect } from 'react';
import CloneVoice from './CloneVoice';
import VoiceLibraryCard from './voiceLibraryCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser } from '@/api/user';
import { deleteVoiceModel, updateVoiceModal } from '@/api/voice';
import { Button, notification, Spin, Tabs, Input, Pagination } from 'antd';
import { TVoiceConfig, TVoiceModel } from '@/types/TVoiceModel';
import { AdminManageFormAdd } from './AdminManageFormAdd';
import LearnMore from '@/components/Buttons/LearnMore';
import { TUTORIALS } from 'src/enums/ETutorials';
import {
	CopyOutlined,
	FilterOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	QuestionCircleOutlined,
	SearchOutlined,
	SoundOutlined,
} from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import VoiceCreditsInfoModal from '@/components/VoiceCreditsInfoModal';
import { useVoiceModels } from 'src/Hooks/useVoiceModels';
import { useClonedVoiceModels } from 'src/Hooks/useClonedVoiceModels';
import { useVoiceFilters } from 'src/Hooks/useVoiceFilters';
import FilterPopup from './components/FilterPopup';
import ActiveFilters from './components/ActiveFilters';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { getResellerPlanById } from '@/api/reseller';
import { getLoggedInUser, getSelectedAgentId } from '@/store/account/selector';
import { calculateCredits, safeJSONParse } from '@/utils/helper';
import { defaultVoiceModel } from '@/utils/defaultVoiceModal';
import Header from '@/components/Header';
interface VoiceSelected {
	voiceId: string;
	provider: string;
	voiceConfig: TVoiceConfig;
}
interface MeterNames {
	freeUsage?: string;
	deepgram?: string;
	cartesia?: string;
	'11labs'?: string;
	rime?: string;
	[key: string]: string | undefined;
}

interface Plan {
	id: string;
	name: string;
	monthly_price: number;
	free_minutes: number;
	freeSMSCredits: number;
	meterNames: MeterNames;
	meterPrices: Record<string, number>;
	featuresSubAccount?: Record<string, any>;
	descriptions: Array<string>;
}

interface PlanResponse {
	plan: Plan;
}

interface VoiceLibraryProps {
	debrand?: boolean;
	isNoviUser?: boolean;
}

const VoiceLibrary = ({ debrand = false, isNoviUser = false }: VoiceLibraryProps) => {
	const resellerData = useAppSelector(getResellerData);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const [provider, setProvider] = useState<string>('All');
	const [searchText, setSearchText] = useState<string>('');
	const [gender, selectedGender] = useState<string>('All');
	const [language, setLanguage] = useState<string>('English');
	const [accent, setAccent] = useState<string>('All');
	const [selectedCard, setSelectedCard] = useState<string | null>(null);
	const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState<number | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [englishVoiceConfig, setEnglishVoiceConfig] = useState<VoiceSelected | null>();
	const [otherVoiceConfig, setOtherVoiceConfig] = useState<VoiceSelected | null>();
	const [showFilterPopup, setShowFilterPopup] = useState(false);
	const [voiceModalCredits, setVoiceModalCredits] = useState<Record<string, number> | null>(null);

	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const { data, isPending, refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});

	const { data: plansData } = useQuery<PlanResponse>({
		queryKey: ['getPlanById'],
		queryFn: () => getResellerPlanById(loggedInUser?.inboundSubscribedProductId as string),
		enabled:
			!!resellerData?.stripe_connect_id &&
			!!loggedInUser?.resellerId &&
			!!loggedInUser?.inboundSubscribedProductId &&
			loggedInUser?.inboundSubscriptionStatus !== 'canceled',
	});

	useEffect(() => {
		if (plansData) {
			if (plansData?.plan?.meterPrices) {
				const credits = calculateCredits(plansData?.plan?.meterPrices || {});
				setVoiceModalCredits(Object.keys(credits).length > 0 ? credits : null);
			}
		}
	}, [plansData]);

	const {
		voiceModels,
		voiceModelsFeatured,
		isPending: isPendingVoiceModels,
		refetch: refetchVoiceModels,
		setPage,
		page,
		totalCount,
		limit,
		setLastEvaluatedKey,
		setForwarding,
	} = useVoiceModels({
		selectedCard,
		provider,
		searchText,
		gender,
		language,
		accent,
	});

	const {
		clonedVoiceModels,
		isPending: isPendingClonedVoiceModels,
		refetch: refetchClonedVoiceModels,
		deleteClonedVoiceMutation,
	} = useClonedVoiceModels(selectedCard);

	const {
		uniqueValues,
		isPending: isPendingVoiceFilters,
		refetch: refetchVoiceFilters,
	} = useVoiceFilters();

	const { mutate: deleteVoiceMutation, isPending: isPendingVoiceModelsDelete } = useMutation({
		mutationKey: ['deleteVoice'],
		mutationFn: deleteVoiceModel,
		onSuccess: ({ success }) => {
			if (success) {
				refetchVoiceModels();
			}
		},
		onError() {
			notification.error({ message: 'Some error occured during voice model updation' });
		},
	});

	const { mutate: updateVoiceModalMutation } = useMutation({
		mutationKey: ['updateVoiceModal'],
		mutationFn: updateVoiceModal,
		onSuccess() {
			refetch();
			refetchVoiceModels();
			notification.success({ message: 'Voice model updated successfully!' });
		},
		onError() {
			notification.error({ message: 'Some error occured during voice model updation' });
		},
	});

	useEffect(() => {
		try {
			if (data?.english_voice_selected || data?.other_language_voice_selected) {
				let englishVoiceSelected: VoiceSelected | null = null;
				let otherLanguageVoiceSelected: VoiceSelected | null = null;
				try {
					if (data.english_voice_selected) {
						englishVoiceSelected = safeJSONParse(data.english_voice_selected, null);
					}
					if (data.other_language_voice_selected) {
						otherLanguageVoiceSelected = safeJSONParse(data.other_language_voice_selected, null);
					}
				} catch {
					notification.error({
						message: 'Something went wrong, please try again later',
					});
				}

				setEnglishVoiceConfig(englishVoiceSelected);
				setOtherVoiceConfig(otherLanguageVoiceSelected);

				if (language === 'All') {
					setSelectedCard(
						englishVoiceSelected?.voiceId || otherLanguageVoiceSelected?.voiceId || null,
					);
				} else if (language === 'English') {
					setSelectedCard(englishVoiceSelected?.voiceId || null);
				} else {
					setSelectedCard(otherLanguageVoiceSelected?.voiceId || null);
				}
			}
		} catch (err) {
			console.error('Error parsing english_voice_selected or other_language_voice_selected:', err);
		}
	}, [data, language, refetch, refetchClonedVoiceModels]);

	const handleFilterChange = (filterType: string, value: string) => {
		setLastEvaluatedKey(null);
		setPage(1);
		setForwarding(true);
		if (filterType === 'provider') {
			setProvider(value);
		} else if (filterType === 'gender') {
			selectedGender(value);
		} else if (filterType === 'language') {
			setLanguage(value);
		} else if (filterType === 'accent') {
			setAccent(value);
		}
	};

	const handleClearFilter = (filterType: string) => {
		setLastEvaluatedKey(null);
		handleFilterChange(filterType, 'All');
	};

	const determineInitialVoiceModel = ({
		voiceId,
		language,
		provider,
		model,
	}: {
		voiceId: string;
		language: string;
		provider: string;
		model?: string;
	}) => {
		if (englishVoiceConfig?.voiceId === voiceId) {
			return (
				englishVoiceConfig?.voiceConfig?.voiceModel ||
				model ||
				defaultVoiceModel({ language, provider })
			);
		} else if (otherVoiceConfig?.voiceId === voiceId) {
			return (
				otherVoiceConfig?.voiceConfig?.voiceModel ||
				model ||
				defaultVoiceModel({ language, provider })
			);
		}
		return model || defaultVoiceModel({ language, provider });
	};

	const handleApplyClick = async (
		cardValue: string,
		selectedVoiceModel: string,
		selectedVoiceProvider: string,
		voiceConfig: TVoiceConfig,
		model?: string,
	) => {
		setSelectedCard(cardValue);
		setPage(1);
		setForwarding(true);
		const voiceSelected = JSON.stringify({
			voiceId: selectedVoiceModel,
			provider: selectedVoiceProvider,
			voiceConfig: voiceConfig || {
				voiceModel: determineInitialVoiceModel({
					voiceId: selectedVoiceModel,
					language,
					provider: selectedVoiceProvider,
					model,
				}),
			},
		});

		const selectedVoice = [...(voiceModelsFeatured || []), ...(voiceModels || [])].find(
			(voice) => voice.voiceId === selectedVoiceModel,
		);

		let targetLanguage = language;
		if (language === 'All' && selectedVoice) {
			targetLanguage =
				selectedVoice.language?.toLowerCase() === 'english' ? 'English' : 'Multilingual';
		}

		updateVoiceModalMutation({
			voiceSelected,
			language: targetLanguage,
			agentId: selectedAgentId as string,
		});
	};

	const handlePlayClick = (index: number | null) => {
		setCurrentlyPlayingIndex(index);
	};

	const handleOpenCreditsInfoModal = () => {
		dispatch(openModal({ modalId: MODAL_IDS.VOICE_CREDITS_INFO }));
	};

	const handleSearchChange = (value: string) => {
		setProvider('All');
		selectedGender('All');
		setLanguage('All');
		setAccent('All');
		setSearchText(value);
	};

	return (
		<Spin spinning={isPending}>
			<Header
				title="Voice Library"
				description="Access and explore your Voice Library in minutes!"
				icon={<SoundOutlined />}
				debrand={debrand}
			/>
			<div className="flex items-center justify-end mb-4 mt-4">
				<LearnMore tutorialId={TUTORIALS.VOICE_LIBRARY} debrand={debrand} />
			</div>
			<VoiceCreditsInfoModal />

			<Tabs
				defaultActiveKey="2"
				className="mt-1"
				items={[
					{
						key: 'VoiceLibrary',
						label: (
							<div
								className="flex items-center space-x-3 px-4 transition-all duration-200"
								data-testid="test-voice-tab"
							>
								<div className="p-2 rounded-lg bg-blue-50 text-blue-500">
									<SoundOutlined className="text-lg" />
								</div>
								<div className="flex flex-col">
									<span className="font-semibold text-gray-800">Voice Library</span>
									<span className="text-xs text-gray-500">Browse all voices</span>
								</div>
							</div>
						),
						children: (
							<Spin
								spinning={
									isPendingVoiceModels || isPendingVoiceFilters || isPendingVoiceModelsDelete
								}
							>
								<main className="w-full px-4 sm:px-6 lg:px-8">
									{/* Credits Info at the top */}
									{!loggedInUser?.resellerClient && data?.number && (
										<div className="mb-6">
											<div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200">
												<InfoCircleOutlined className="text-blue-500 text-lg" />
												<p className="text-sm text-gray-600 flex-1 mt-4">
													Voice credits are consumed whenever your AI receptionist speaks during
													calls.
												</p>
												<Button
													onClick={handleOpenCreditsInfoModal}
													type="link"
													icon={<QuestionCircleOutlined />}
													className="text-blue-600"
												>
													Learn More
												</Button>
											</div>
										</div>
									)}

									{/* Featured Voices Section */}
									<div className="mb-8">
										<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
											{page === 1 && (
												<div className="flex items-center gap-3">
													<h2 className="text-lg sm:text-xl font-bold text-gray-900">
														Featured Voices
													</h2>
												</div>
											)}
											{data?.isSuperAdmin && (
												<Button
													type="primary"
													onClick={() => setIsModalOpen(true)}
													className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 border-0 rounded-lg"
													icon={<PlusOutlined />}
												>
													Add New Voice
												</Button>
											)}
										</div>
										{page === 1 && (
											<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
												{voiceModelsFeatured?.map((item, index) => (
													<VoiceLibraryCard
														key={item.voiceId}
														index={index}
														data={item}
														isSelected={selectedCard === item.voiceId}
														englishVoiceConfig={englishVoiceConfig}
														otherVoiceConfig={otherVoiceConfig}
														handleApplyClick={handleApplyClick}
														onApplyClick={() =>
															handleApplyClick(
																item.voiceId,
																item.voiceId,
																item.provider,
																item?.voiceConfig,
																item.model,
															)
														}
														currentlyPlayingIndex={currentlyPlayingIndex}
														onPlayClick={handlePlayClick}
														user={data}
														deleteVoiceMutation={(params) =>
															deleteVoiceMutation({
																...params,
																agentId: selectedAgentId,
															})
														}
														isFeatured={true}
														voiceModalCredits={voiceModalCredits}
													/>
												))}
											</div>
										)}
									</div>

									{/* All Voices Section */}
									<div>
										<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-0">
											<h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-0">
												All Voices
											</h2>
											<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
												<div className="relative flex-grow sm:flex-grow-0">
													<Input
														placeholder="Search voices..."
														allowClear
														className="w-full sm:w-64 border-black"
														value={searchText}
														onChange={(e) => handleSearchChange(e.target.value)}
														prefix={<SearchOutlined className="text-gray-400" />}
													/>
												</div>
												<FilterPopup
													visible={showFilterPopup}
													onClose={() => setShowFilterPopup(false)}
													filters={{
														provider: uniqueValues.provider,
														gender: uniqueValues.gender,
														language: uniqueValues.language,
														accent: uniqueValues.accent,
													}}
													selectedFilters={{
														provider,
														gender,
														language,
														accent,
													}}
													onFilterChange={handleFilterChange}
												>
													<Button
														icon={<FilterOutlined />}
														onClick={() => setShowFilterPopup(true)}
														className={`
                               bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white
                               transition-all w-full sm:w-auto justify-center
                               ${
																	provider !== 'All' ||
																	gender !== 'All' ||
																	language !== 'English' ||
																	accent !== 'All'
																		? 'bg-gray-900 text-black hover:bg-gray-800'
																		: ''
																}
                              `}
													>
														Filters
													</Button>
												</FilterPopup>
											</div>
										</div>

										<p className="text-sm text-gray-600 mb-3">
											Browse our complete collection of voices
										</p>

										<ActiveFilters
											provider={provider}
											gender={gender}
											language={language}
											accent={accent}
											onClearFilter={handleClearFilter}
										/>

										<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
											{voiceModels?.map((item, index) => (
												<VoiceLibraryCard
													key={item.voiceId}
													index={index + (voiceModelsFeatured?.length || 0)}
													data={item}
													isSelected={selectedCard === item.voiceId}
													englishVoiceConfig={englishVoiceConfig}
													otherVoiceConfig={otherVoiceConfig}
													handleApplyClick={handleApplyClick}
													onApplyClick={() =>
														handleApplyClick(
															item.voiceId,
															item.voiceId,
															item.provider,
															item?.voiceConfig,
															item.model,
														)
													}
													currentlyPlayingIndex={currentlyPlayingIndex}
													onPlayClick={handlePlayClick}
													user={data}
													deleteVoiceMutation={(params) =>
														deleteVoiceMutation({
															...params,
															agentId: selectedAgentId,
														})
													}
													isFeatured={false}
													voiceModalCredits={voiceModalCredits}
												/>
											))}
										</div>
										<div className="flex justify-center py-6">
											<Pagination
												current={page}
												pageSize={limit}
												total={totalCount}
												onChange={(newPage) => {
													if (newPage > page) {
														setForwarding(true);
													} else if (page > newPage) {
														setForwarding(false);
													}
													setPage(newPage);
												}}
												showSizeChanger={false}
											/>
										</div>
									</div>
								</main>
							</Spin>
						),
					},
					{
						key: 'CloneVoice',
						label: (
							<div
								className="flex items-center space-x-3 px-4 transition-all duration-200"
								data-testid="test-clone-tab"
							>
								<div className="p-2 rounded-lg bg-purple-50 text-purple-500">
									<CopyOutlined className="text-lg" />
								</div>
								<div className="flex flex-col">
									<span className="font-semibold text-gray-800">Clone Voice</span>
									<span className="text-xs text-gray-500">Create custom voices</span>
								</div>
							</div>
						),
						children: (
							<Spin spinning={isPendingClonedVoiceModels}>
								<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
									<CloneVoice
										data={clonedVoiceModels}
										refetch={() => {
											refetch();
											refetchClonedVoiceModels();
											refetchVoiceFilters();
										}}
										isNoviUser={isNoviUser}
									/>
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
										{clonedVoiceModels?.data?.map((item: TVoiceModel, index: number) => (
											<VoiceLibraryCard
												key={item.voiceId}
												index={index}
												data={item}
												isSelected={selectedCard === item.voiceId}
												englishVoiceConfig={englishVoiceConfig}
												otherVoiceConfig={otherVoiceConfig}
												handleApplyClick={handleApplyClick}
												onApplyClick={() =>
													handleApplyClick(
														item.voiceId,
														item.voiceId,
														item.provider,
														item?.voiceConfig,
														item.model,
													)
												}
												currentlyPlayingIndex={currentlyPlayingIndex}
												onPlayClick={handlePlayClick}
												user={data}
												deleteVoiceMutation={(params) =>
													deleteClonedVoiceMutation({
														...params,
														agentId: selectedAgentId as string,
													})
												}
												isFeatured={false}
											/>
										))}
									</div>
								</div>
							</Spin>
						),
					},
				]}
			/>

			<AdminManageFormAdd
				refetch={refetchVoiceModels}
				addMode={true}
				openModal={isModalOpen}
				setOpenModal={setIsModalOpen}
				uniqueAccents={uniqueValues.accent}
			/>
		</Spin>
	);
};

export default VoiceLibrary;
